import type { DeviceId, LiveDeviceState } from '@kidshub/contracts';
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';

/**
 * Keeps one device's live state fresh enough to act on.
 *
 * Polled, not streamed, and that is a decision rather than a shortcut. The
 * tablet needs a stream because a pause has to arrive *now*; the dashboard is a
 * picture, and a picture a few seconds behind is not wrong. Adding a
 * parent-facing stream would also mean a second subscriber key on a realtime
 * hub that is still in-process — and therefore already wrong the moment a
 * second backend instance exists. Building on that before it is fixed is the
 * wrong order.
 *
 * Two intervals, both stopped while the tab is hidden:
 *
 * - the poll, which asks the server what is true;
 * - a one-second tick, which only moves the clock the countdown reads.
 *
 * Stopping on hidden matters more than it looks: a dashboard left open in a
 * background tab overnight is otherwise several thousand requests nobody reads.
 * Coming back visible polls immediately rather than waiting out the interval,
 * so a parent returning to the tab sees the truth and not a stale picture.
 */

export const POLL_INTERVAL_MS = 10_000;
const TICK_INTERVAL_MS = 1_000;

export interface LiveDeviceStateHandle {
  state: LiveDeviceState | null;
  /** When the held state arrived, so the countdown knows how far to project. */
  polledAtMs: number;
  /** Advances every second while the tab is visible. */
  nowMs: number;
  errorKey: string | null;
  refresh: () => void;
}

const isVisible = () => document.visibilityState === 'visible';

export const useLiveDeviceState = (deviceId: DeviceId): LiveDeviceStateHandle => {
  const [state, setState] = useState<LiveDeviceState | null>(null);
  const [polledAtMs, setPolledAtMs] = useState(() => Date.now());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const poll = useCallback(async () => {
    try {
      const next = await apiClient.deviceState.get({ deviceId });
      setState(next);
      setPolledAtMs(Date.now());
      setNowMs(Date.now());
      setErrorKey(null);
    } catch (error) {
      // A failed poll leaves the last picture on screen rather than blanking
      // it. What it must not do is leave the parent thinking it is current.
      setErrorKey(toErrorKey(error));
    }
  }, [deviceId]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (interval === null) {
        interval = setInterval(() => void poll(), POLL_INTERVAL_MS);
      }
    };

    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const onVisibilityChange = () => {
      if (isVisible()) {
        void poll();
        start();
      } else {
        stop();
      }
    };

    void poll();
    if (isVisible()) {
      start();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [poll]);

  useEffect(() => {
    const tick = setInterval(() => {
      if (isVisible()) {
        setNowMs(Date.now());
      }
    }, TICK_INTERVAL_MS);

    return () => {
      clearInterval(tick);
    };
  }, []);

  return {
    state,
    polledAtMs,
    nowMs,
    errorKey,
    refresh: () => {
      void poll();
    },
  };
};
